import { createContext, useContext, useState, useEffect, useMemo, useRef } from "react";
import { useParams, useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { createOrgCacheGuard, flushForgottenCustomerData } from "@/lib/orgQueryCache";
import { AlertCircle } from "lucide-react";

export interface Organization {
  id: number;
  name: string;
  slug: string;
  logoUrl?: string | null;
  faviconUrl?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  accentColor?: string | null;
  companyEmail?: string | null;
  companyPhone?: string | null;
  companyAddress?: string | null;
  description?: string | null;
  isActive: boolean;
  isDemo?: boolean;
  /** Kiosk mode is a paid add-on; off hides every kiosk entry point. */
  kioskEnabled?: boolean;
}

interface OrganizationContextValue {
  organization: Organization | null;
  orgSlug: string;
  isLoading: boolean;
  error: string | null;
  apiBasePath: string;
}

export const OrganizationContext = createContext<OrganizationContextValue | null>(null);

const DEFAULT_ORG_SLUG = "cn-produce";

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const params = useParams<{ orgSlug?: string }>();
  const [location] = useLocation();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The slug `organization` / `error` answer for. Until it matches the current slug the
  // org is loading — from the very render the slug changes, not one effect later, so
  // OrganizationGuard unmounts the old org's page at once. Otherwise that page renders
  // once more under the new slug with its old state (the previous org's signed-in
  // account) and fires reads at the new org for an account it does not have.
  const [loadedSlug, setLoadedSlug] = useState<string | null>(null);

  const orgSlug = useMemo(() => {
    // window.location.pathname is the source of truth for org slug. When the
    // URL has /org/:slug/ we write through to sessionStorage so other readers
    // (admin-route, queryClient) stay coherent — same URL-first writeback
    // pattern as admin-route.tsx.
    const windowMatch = window.location.pathname.match(/^\/org\/([^/]+)/);
    if (windowMatch) {
      const slug = windowMatch[1];
      sessionStorage.setItem('currentOrgSlug', slug);
      return slug;
    }

    // Wouter params / location can also produce a slug — same writeback rule.
    if (params.orgSlug) {
      sessionStorage.setItem('currentOrgSlug', params.orgSlug);
      return params.orgSlug;
    }
    const pathParts = location.split('/').filter(Boolean);
    if (pathParts.length >= 2 && pathParts[0] === 'org') {
      const slug = pathParts[1];
      sessionStorage.setItem('currentOrgSlug', slug);
      return slug;
    }

    // No org-scoped URL — do NOT silently inherit a stale sessionStorage value.
    // Two tabs to two different orgs share sessionStorage; reading it here
    // would let tab B's last write determine tab A's tenant context. Fall
    // through to the default instead.
    return DEFAULT_ORG_SLUG;
  }, [params.orgSlug, location]);

  // A different org: drop every cached query before any child renders under the new
  // slug. During render, not in an effect — an effect runs after the children have
  // already rendered the old org's cached categories, market report and menu.
  // Ref-guarded and idempotent for one slug, so a repeated render clears nothing.
  const queryClient = useQueryClient();
  const orgCacheGuard = useRef<ReturnType<typeof createOrgCacheGuard> | null>(null);
  if (!orgCacheGuard.current) orgCacheGuard.current = createOrgCacheGuard(queryClient);
  orgCacheGuard.current(orgSlug);

  const apiBasePath = useMemo(() => `/org/${orgSlug}/api`, [orgSlug]);

  // A customer sign-out recorded what to forget; forget it once the route has changed and
  // the signed-out page has unmounted (lib/orgQueryCache.ts). Passive effects of a commit run
  // after every unmount cleanup in it, so the old page's queries have no observers here.
  useEffect(() => {
    flushForgottenCustomerData(queryClient);
  }, [location, queryClient]);

  useEffect(() => {
    // The slug can change while a load is in flight: /demo renders under the default
    // slug, then redirects to /org/demo-public/... A slower 404 for the old slug must
    // not land after the new org has loaded and show "Organization Not Found".
    let stale = false;

    async function fetchOrganization() {
      if (!orgSlug) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        
        const response = await fetch(`/org/${orgSlug}/api/organization`);
        if (stale) return;

        if (response.ok) {
          const org = await response.json();
          if (stale) return;
          setOrganization(org);
          setLoadedSlug(orgSlug);
        } else if (response.status === 404) {
          setError("Organization not found");
          setOrganization(null);
          setLoadedSlug(orgSlug);
        } else {
          setError("Failed to load organization");
          setOrganization(null);
          setLoadedSlug(orgSlug);
        }
      } catch (err) {
        if (stale) return;
        console.error("Error fetching organization:", err);
        setError("Failed to load organization");
        setOrganization(null);
        setLoadedSlug(orgSlug);
      } finally {
        if (!stale) setIsLoading(false);
      }
    }

    fetchOrganization();
    return () => {
      stale = true;
    };
  }, [orgSlug]);

  // Update favicon dynamically when organization changes
  useEffect(() => {
    // Only update favicon/title if we're on an org-scoped route
    const isOrgRoute = window.location.pathname.startsWith('/org/');
    
    const faviconLink = document.getElementById('dynamic-favicon') as HTMLLinkElement;
    if (isOrgRoute && faviconLink && organization?.faviconUrl) {
      faviconLink.href = organization.faviconUrl;
    } else if (faviconLink && !isOrgRoute) {
      faviconLink.href = '/favicon.ico';
    }
    
    // Also update page title with organization name (only on org routes)
    if (isOrgRoute && organization?.name) {
      document.title = `${organization.name} | FreshOrder`;
    }
  }, [organization]);

  const value = useMemo(() => ({
    organization,
    orgSlug,
    isLoading: isLoading || loadedSlug !== orgSlug,
    error,
    apiBasePath,
  }), [organization, orgSlug, isLoading, loadedSlug, error, apiBasePath]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return context;
}

export function useApiPath(path: string) {
  const { apiBasePath } = useOrganization();
  return `${apiBasePath}${path.startsWith('/') ? path : `/${path}`}`;
}

export function OrganizationGuard({ children }: { children: React.ReactNode }) {
  const { organization, isLoading, error, orgSlug } = useOrganization();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100" data-testid="org-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading organization...</p>
        </div>
      </div>
    );
  }

  if (error || !organization) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100" data-testid="org-error">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="org-error-title">
            Organization Not Found
          </h1>
          <p className="text-gray-600 mb-4" data-testid="org-error-message">
            The organization "{orgSlug}" could not be found. Please check the URL and try again.
          </p>
          <a 
            href="/" 
            className="inline-block bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors"
            data-testid="org-error-home-link"
          >
            Go to Home
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
